import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';

const teamMembers = [
  {
    name: 'H Boss',
    role: 'Web Master, Grower & Boss',
    bio: 'The technical backbone of Elixir Sue. H Boss combines deep hands-on cultivation expertise with cutting-edge web mastery to bring our global community the best digital and growing experience possible.',
    specialty: 'Technical Expertise & Hands-On Growing',
    icon: 'ri-code-s-slash-line',
    image: 'https://static.readdy.ai/image/175fc0d3a7deb119f46e9d68342e64a7/4776185435a9db7cfffbaf91ef9e7a3d.png',
    skills: ['Web Development', 'Cannabis Cultivation', 'Organic Methods', 'Team Leadership'],
    color: '#2D5F3F'
  },
  {
    name: 'A Boss',
    role: 'Creative Inspiration',
    bio: 'The artistic soul of Elixir Sue. A Boss brings a unique creative vision shaped by a lifelong love of classic rock and natural aesthetics, crafting the brand identity that resonates with wellness seekers worldwide.',
    specialty: 'Artistic Vision & Brand Identity',
    icon: 'ri-palette-line',
    image: 'https://static.readdy.ai/image/175fc0d3a7deb119f46e9d68342e64a7/6edb782bc487e1b8cdb445ca63138a93.png',
    skills: ['Creative Direction', 'Brand Design', 'Visual Storytelling', 'Classic Rock Enthusiast'],
    color: '#D4AF37'
  },
  {
    name: 'HR Boss',
    role: 'Master Grower',
    bio: 'The green thumb behind our cultivation philosophy. HR Boss is a seasoned organic farming specialist with unmatched expertise in worm cultivation and sustainable soil science, ensuring every product meets the highest natural standards.',
    specialty: 'Organic Farming & Worm Cultivation',
    icon: 'ri-plant-line',
    image: 'https://static.readdy.ai/image/175fc0d3a7deb119f46e9d68342e64a7/33c1ef5d5efb4dcf8ea48e0b184ea0e0.png',
    skills: ['Organic Farming', 'Worm Cultivation', 'Soil Science', 'Sustainable Practices'],
    color: '#2D5F3F'
  }
];

const values = [
  { icon: 'ri-leaf-line', title: 'Natural & Sustainable', desc: 'Every product and practice rooted in organic, earth-friendly methods that respect the planet.' },
  { icon: 'ri-book-open-line', title: 'Knowledge Sharing', desc: 'We believe education empowers growers at every level — from first-time cultivators to seasoned experts.' },
  { icon: 'ri-shield-check-line', title: 'Integrity for All Markets', desc: 'Transparent, honest, and compliant across every region we serve worldwide.' },
  { icon: 'ri-global-line', title: 'Global Community', desc: 'Connecting wellness seekers and growers from every corner of the world under one shared passion.' }
];

export default function AboutPage() {
  const heroAnim = useScrollAnimation();
  const storyAnim = useScrollAnimation();
  const teamAnim = useStaggerAnimation(3);
  const valuesAnim = useStaggerAnimation(4);
  const quoteAnim = useScrollAnimation();
  const communityAnim = useScrollAnimation();

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative h-[70vh] w-full flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9a5863801f53ce2511adf06a59ab1f7c.jpg"
            alt="Elixir Sue Farm"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto w-full">
          <div className="inline-block px-5 py-2 border border-[#D4AF37]/70 rounded-full text-[#D4AF37] text-sm font-semibold tracking-widest uppercase mb-6">
            Our Story
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Passionate Experts.<br />Global Wellness.
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Meet the dedicated team behind Elixir Sue — blending decades of cultivation expertise with a shared mission to educate, inspire, and deliver the finest cannabis wellness products worldwide.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section
        ref={storyAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 md:py-28 px-4"
        style={{
          opacity: storyAnim.isVisible ? 1 : 0,
          transform: storyAnim.isVisible ? 'translateY(0)' : 'translateY(50px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4 block">Who We Are</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A3A2A] mb-6 md:mb-8 leading-tight">
              Your Trusted Global Cannabis Experts
            </h2>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-4 md:mb-6">
              We are passionate about cannabis cultivation and natural consumption. Founded on the belief that everyone deserves access to premium organic products and expert knowledge, Elixir Sue has grown into a worldwide community of growers, wellness seekers, and lifelong learners.
            </p>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 md:mb-10">
              Our mission is simple: <strong className="text-[#2D5F3F]">educate, inspire, and deliver</strong> the finest products and cultivation techniques to wellness seekers everywhere — from novice growers taking their first steps to seasoned experts pushing the boundaries of organic farming.
            </p>
            <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6">
              {[
                { number: '50K+', label: 'Global Customers' },
                { number: '25+', label: 'Years Experience' },
                { number: '100%', label: 'Organic Methods' }
              ].map((stat, i) => (
                <div key={i} className="text-center p-3 sm:p-4 md:p-5 bg-[#F8FAF9] rounded-xl border border-[#2D5F3F]/10">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#2D5F3F] mb-1">{stat.number}</div>
                  <div className="text-[10px] sm:text-xs text-gray-500 uppercase tracking-wide font-semibold">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative order-first lg:order-last">
            <div className="rounded-3xl overflow-hidden h-[400px] sm:h-[450px] md:h-[520px] shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=three%20people%20working%20together%20in%20a%20bright%20modern%20cannabis%20greenhouse%20examining%20healthy%20organic%20plants%20with%20clipboards%20and%20tools%20collaborative%20team%20environment%20natural%20sunlight%20warm%20earthy%20tones%20professional%20photography%20authentic%20teamwork%20sustainable%20farming&width=700&height=800&seq=about_story_01&orientation=portrait"
                alt="Elixir Sue Team"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 sm:-bottom-6 sm:-left-6 bg-[#2D5F3F] text-white p-4 sm:p-6 rounded-2xl shadow-xl">
              <div className="text-2xl sm:text-3xl font-bold mb-1">2000</div>
              <div className="text-xs sm:text-sm text-white/80">Founded with passion</div>
            </div>
            <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-[#D4AF37] text-[#1A3A2A] p-4 sm:p-6 rounded-2xl shadow-xl">
              <i className="ri-global-line text-2xl sm:text-3xl block mb-1"></i>
              <div className="text-xs sm:text-sm font-bold">Worldwide Shipping</div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section
        ref={teamAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 md:py-28 px-4 bg-[#F8FAF9]"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4 block">The People Behind the Brand</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A3A2A]">Meet the Team</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-3 transition-all duration-500 group"
                style={{
                  opacity: teamAnim.visibleItems[index] ? 1 : 0,
                  transform: teamAnim.visibleItems[index] ? 'translateY(0)' : 'translateY(50px)',
                  transition: 'opacity 0.7s ease-out, transform 0.7s ease-out, box-shadow 0.4s, translate 0.4s',
                  transitionDelay: `${index * 150}ms`
                }}
              >
                {/* Photo */}
                <div className="relative h-64 sm:h-72 md:h-80 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400"></div>
                  <div
                    className="absolute top-5 right-5 w-12 h-12 flex items-center justify-center rounded-full text-white"
                    style={{ backgroundColor: member.color }}
                  >
                    <i className={`${member.icon} text-xl`}></i>
                  </div>
                </div>

                {/* Info */}
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1A3A2A] mb-1">{member.name}</h3>
                  <p className="text-sm font-semibold uppercase tracking-wide mb-1" style={{ color: member.color }}>
                    {member.role}
                  </p>
                  <p className="text-xs text-gray-400 italic mb-4">{member.specialty}</p>
                  <p className="text-sm text-gray-600 leading-relaxed mb-6">{member.bio}</p>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2">
                    {member.skills.map((skill, si) => (
                      <span
                        key={si}
                        className="px-3 py-1 text-xs font-semibold rounded-full"
                        style={{
                          backgroundColor: `${member.color}15`,
                          color: member.color
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section
        ref={valuesAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 md:py-28 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-16 md:mb-20">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4 block">What Drives Us</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A3A2A]">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((val, index) => (
              <div
                key={index}
                className="text-center p-8 sm:p-10 rounded-2xl border border-[#2D5F3F]/10 hover:border-[#D4AF37] hover:shadow-xl hover:-translate-y-2 transition-all duration-400 group bg-white"
                style={{
                  opacity: valuesAnim.visibleItems[index] ? 1 : 0,
                  transform: valuesAnim.visibleItems[index] ? 'translateY(0)' : 'translateY(40px)',
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out, box-shadow 0.3s, border-color 0.3s, translate 0.3s',
                  transitionDelay: `${index * 120}ms`
                }}
              >
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 rounded-full bg-[#2D5F3F]/10 group-hover:bg-[#D4AF37]/15 transition-colors duration-300">
                  <i className={`${val.icon} text-3xl text-[#2D5F3F] group-hover:text-[#D4AF37] transition-colors duration-300`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-[#1A3A2A] mb-3">{val.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section
        ref={quoteAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 md:py-24 px-4 relative overflow-hidden"
        style={{
          opacity: quoteAnim.isVisible ? 1 : 0,
          transform: quoteAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.9s ease-out, transform 0.9s ease-out'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A3A2A] to-[#2D5F3F]"></div>
        <div className="absolute inset-0 opacity-10">
          <i className="ri-double-quotes-l text-[300px] sm:text-[400px] md:text-[500px] text-white absolute -top-20 -left-20 leading-none"></i>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <i className="ri-double-quotes-l text-4xl sm:text-5xl md:text-6xl text-[#D4AF37] mb-6 sm:mb-8 block"></i>
          <blockquote className="text-2xl sm:text-3xl md:text-4xl font-light text-white leading-relaxed italic mb-6 sm:mb-8">
            Blending ancient wisdom with modern innovation for global wellness.
          </blockquote>
          <cite className="text-[#D4AF37] font-semibold text-base sm:text-lg not-italic tracking-wide">
            — The Elixir Sue Team
          </cite>
        </div>
      </section>

      {/* Community CTA */}
      <section
        ref={communityAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 md:py-28 px-4"
        style={{
          opacity: communityAnim.isVisible ? 1 : 0,
          transform: communityAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest mb-4 block">Be Part of Something Bigger</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A3A2A] mb-6">
            Join Thousands Worldwide
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-8 sm:mb-10 md:mb-12 max-w-2xl mx-auto leading-relaxed">
            Share your grows, learn from our experts, and connect with a passionate global community of cannabis wellness enthusiasts.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/shop"
              className="w-full sm:w-auto group bg-[#2D5F3F] text-white px-8 sm:px-10 py-4 rounded-full text-base font-semibold hover:bg-[#D4AF37] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
            >
              Explore Products
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
            </Link>
            <Link
              to="/blog"
              className="w-full sm:w-auto group border-2 border-[#2D5F3F] text-[#2D5F3F] px-8 sm:px-10 py-4 rounded-full text-base font-semibold hover:bg-[#2D5F3F] hover:text-white hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line text-xl"></i>
              Learn from Experts
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}